import { useEffect, useState } from 'react';
import {
  ButtonPrimary,
  ButtonSecondary,
  ButtonSize,
  ClickOutsideDetector,
  formatDate,
  IconCollapse,
  IconDots,
  IconEdit,
  IconExpand,
  IconRefresh,
  IconSandWatch,
  Popup,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  AmlAction,
  ClientScoreTypeEnum,
  CustomerScoreUpdateStatus,
  NewApplicationStatus,
  RiskScoringTypes,
} from '../../hoc/customers/types';
import ExpandableRow from '../partial/aml/expandable-row';
import ExpandableRowContent from '../partial/aml/expandable-row-content';
import TypeOfClient from '../partial/aml/type-of-client';
import DeliveryChannels from '../partial/aml/delivery-channels';
import Products from '../partial/aml/products';
import SourceOfFunds from '../partial/aml/source-of-funds';
import { ApplicationRiskScoreTypes } from './types';
import { ApplicationType } from '../../hoc/new-application/types';
import DateOfIncorporation from '../partial/aml/date-of-incorporation';
import SicCodes from '../partial/aml/sic-codes';

import cn from './new-application.module.css';

const ApplicationRiskScore = ({
  aml,
  type,
  isEditable,
  status,
  updateIsSuccess,
  requestInProgress,
  calculateAllowed,
  amlUpdateStatus,
  onUpdate,
  riskScoreIsLoading,
  manualScoreAllowed,
  users,
  changeLogsAvailable,
  setRiskScoreChangeLogsSidepanelVisible,
  setManualScoreSidepanelVisible,
}: ApplicationRiskScoreTypes) => {
  const [expandedRows, setExpandedRows] = useState<string[]>([
    RiskScoringTypes.DELIVERY_CHANNEL,
    RiskScoringTypes.GEOGRAPHICAL_RISK,
    RiskScoringTypes.PRODUCTS,
    RiskScoringTypes.TYPE_OF_CLIENT,
    RiskScoringTypes.SOURCE_OF_FUNDS,
    RiskScoringTypes.MONTHLY_VOLUMES,
    RiskScoringTypes.SIC_CODES,
    RiskScoringTypes.DATE_ON_INCORPORATION,
  ]);
  const [blockCollapsed, setBlockCollapsed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const [riskScoreMenuVisible, setRiskScoreMenuVisible] = useState(false);

  const {
    countries,
    monthlyTurnover,
    riskScoreRanges,
    monthlyTurnoverCorporate,
  } = useAppSelector((state) => state.customersDictionaries);

  const onExpandClick = () => {
    if (expandedRows.length) {
      setExpandedRows([]);
    } else {
      setExpandedRows([
        RiskScoringTypes.DELIVERY_CHANNEL,
        RiskScoringTypes.GEOGRAPHICAL_RISK,
        RiskScoringTypes.PRODUCTS,
        RiskScoringTypes.TYPE_OF_CLIENT,
        RiskScoringTypes.SOURCE_OF_FUNDS,
        RiskScoringTypes.MONTHLY_VOLUMES,
        RiskScoringTypes.SIC_CODES,
        RiskScoringTypes.DATE_ON_INCORPORATION,
      ]);
    }
  };

  let totalRiskClassName: string;

  const riskLevel = riskScoreRanges?.find(
    (item) =>
      (aml?.totalRiskScore as number) >= item.rangeStart &&
      (aml?.totalRiskScore as number) <= item.rangeEnd,
  );

  if (riskLevel?.action === AmlAction.NONE) {
    totalRiskClassName = cn.totalRiskScoringGood;
  } else if (riskLevel?.action === AmlAction.OPS_REVIEW) {
    totalRiskClassName = cn.totalRiskScoringMedium;
  } else {
    totalRiskClassName = cn.totalRiskScoringLow;
  }

  useEffect(() => {
    if (updateIsSuccess) {
      setPopupVisible(false);
    }
  }, [updateIsSuccess]);

  useEffect(() => {
    if (aml?.clientScoreType === ClientScoreTypeEnum.MANUAL) {
      setBlockCollapsed(false);

      setExpanded(false);
    }
  }, [aml]);

  return (
    <>
      <div
        className={`${cn.newApplicationRiskScore} ${
          blockCollapsed ? cn.totalRiskScoringTopRounded : ''
        } ${
          !riskScoreIsLoading &&
          !aml &&
          status === NewApplicationStatus.INCOMPLETE &&
          requestInProgress
            ? cn.unfilled
            : ''
        }`}
      >
        <button
          type="button"
          aria-label="Collapse button"
          className={cn.totalRiskCollapseButton}
          onClick={() => {
            setBlockCollapsed(!blockCollapsed);
            if (!expanded) {
              setExpanded(true);
            } else if (expanded && blockCollapsed) {
              setExpanded(false);
            }
          }}
          disabled={Boolean(
            aml?.clientScoreType === ClientScoreTypeEnum.MANUAL,
          )}
        />
        {!riskScoreIsLoading &&
          !aml &&
          status === NewApplicationStatus.INCOMPLETE && (
            <>
              <div className={cn.newApplicationRiskScoreIconWrapper}>
                <IconSandWatch />
              </div>
              <span className={cn.noRiskScoreText}>
                Customer Risk Score will be calculated after the client fills
                out the application
              </span>
            </>
          )}
        {riskScoreIsLoading && !aml && (
          <div className={cn.riskScoreTotalText}>
            Customer Application Risk Score is Loading...
          </div>
        )}
        {!riskScoreIsLoading &&
          !aml &&
          status !== NewApplicationStatus.INCOMPLETE && (
            <div className={cn.riskScoreTotalText}>
              Customer Application Risk Score is calculating...
            </div>
          )}
        <div className={cn.riskTotalWrapper}>
          <div className={cn.riskTotalLeft}>
            {aml && (
              <>
                <div
                  className={`${cn.riskScoreTotalValue} ${totalRiskClassName}`}
                >
                  {aml?.totalRiskScore}
                </div>
                <div
                  className={`${cn.riskScoreLevel} ${totalRiskClassName}`}
                >{`${riskLevel?.name.toLowerCase() || 'High'} Risk`}</div>
                <div className={cn.totalRiskScoringType}>
                  {aml?.clientScoreType === ClientScoreTypeEnum.AUTO ? (
                    <span>
                      <IconRefresh width={14} height={14} /> auto
                    </span>
                  ) : (
                    <span>
                      <IconEdit width={14} height={14} /> manually
                    </span>
                  )}
                </div>
                <div className={cn.totalRiskScoringDescription}>
                  <div className={cn.totalRiskScoringCalculatedDate}>
                    {aml?.updatedAt || aml?.createdAt ? (
                      <span>
                        {formatDate(aml?.updatedAt || aml?.createdAt, true)}
                      </span>
                    ) : (
                      ''
                    )}
                  </div>
                  {aml?.employeeId && (
                    <>
                      <div className={cn.amlCircle} />
                      <div className={cn.riskScoringEmployee}>
                        {
                          users?.find((item) => item.value === aml?.employeeId)
                            ?.name
                        }
                      </div>
                    </>
                  )}
                </div>
                {amlUpdateStatus === CustomerScoreUpdateStatus.FAIL && (
                  <div className={cn.updateStatusFailed}>
                    Update Customer Application Risk Score Failed. Try again
                  </div>
                )}
              </>
            )}
            {amlUpdateStatus === CustomerScoreUpdateStatus.FAIL && !aml && (
              <div className={cn.statusFailed}>
                Update Customer Application Risk Score Failed. Try again
              </div>
            )}
            {amlUpdateStatus === CustomerScoreUpdateStatus.QUEUED && !aml && (
              <div className={cn.riskScoreTotalText}>
                The customer&apos;s risk score is calculating...
              </div>
            )}
          </div>
          <div className={cn.riskTotalRight}>
            {manualScoreAllowed &&
              status !== NewApplicationStatus.ACCEPTED &&
              status !== NewApplicationStatus.DECLINED && (
                <div className={cn.totalRiskScoringButtonWrapper}>
                  <ButtonSecondary
                    size={ButtonSize.SMALL}
                    text="Set manually"
                    className={`button-white ${cn.setRiskScoreButton}`}
                    icon={<IconEdit />}
                    onClick={() => setManualScoreSidepanelVisible(true)}
                  />
                </div>
              )}
            {calculateAllowed && (
              <div className={cn.totalRiskScoringButtonWrapper}>
                {status !== NewApplicationStatus.ACCEPTED &&
                  status !== NewApplicationStatus.DECLINED && (
                    <ButtonPrimary
                      icon={
                        amlUpdateStatus !== CustomerScoreUpdateStatus.QUEUED ? (
                          <IconRefresh />
                        ) : undefined
                      }
                      size={ButtonSize.SMALL}
                      text={
                        amlUpdateStatus === CustomerScoreUpdateStatus.QUEUED
                          ? 'Updating...'
                          : 'Auto update'
                      }
                      className={cn.updateRiskScoreButton}
                      onClick={() =>
                        amlUpdateStatus !== CustomerScoreUpdateStatus.QUEUED &&
                        setPopupVisible(true)
                      }
                    />
                  )}
              </div>
            )}
            {changeLogsAvailable && (
              <div className={cn.riskScoreMenuWrapper}>
                <ClickOutsideDetector
                  setOptionsListVisible={setRiskScoreMenuVisible}
                >
                  <>
                    <button
                      type="button"
                      className={`${cn.riskScoreMenuButton} ${
                        riskScoreMenuVisible ? cn.active : ''
                      }`}
                      onClick={() =>
                        setRiskScoreMenuVisible(!riskScoreMenuVisible)
                      }
                    >
                      <IconDots />
                    </button>
                    {riskScoreMenuVisible && (
                      <div className={cn.riskScoreMenu}>
                        <button
                          type="button"
                          className={cn.riskScoreMenuListButton}
                          onClick={() => {
                            setRiskScoreChangeLogsSidepanelVisible(true);
                            setRiskScoreMenuVisible(false);
                          }}
                        >
                          Change Log
                        </button>
                      </div>
                    )}
                    <div />
                  </>
                </ClickOutsideDetector>
              </div>
            )}
          </div>
        </div>
      </div>

      {blockCollapsed && (
        <div className={cn.scoreResultWrapper}>
          <div className={cn.collapseButtonWrapper}>
            <button
              type="button"
              className={cn.collapseAllButton}
              onClick={onExpandClick}
            >
              {!expandedRows?.length ? 'Expand all' : 'Collapse all'}
              {!expandedRows?.length ? (
                <IconExpand width={15} height={15} className={cn.iconExpand} />
              ) : (
                <IconCollapse width={15} className={cn.iconCollapse} />
              )}
            </button>
          </div>
          <ExpandableRow
            name={RiskScoringTypes.GEOGRAPHICAL_RISK}
            title="Geographical risk"
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            totalRisk={aml?.geographicalRiskTotal as number}
            // comments={[]}
            // onSend={() => {}}
            // onDelete={() => {}}
          >
            {type === ApplicationType.INDIVIDUAL && (
              <>
                <ExpandableRowContent
                  title="Residency:"
                  value={aml?.countryId as string}
                  risk={
                    aml?.countryRisk?.toString() !== 'null' &&
                    aml?.countryRisk?.toString() !== 'undefined'
                      ? (aml?.countryRisk?.toString() as string)
                      : '-'
                  }
                  list={countries}
                  name="residency"
                  isEditable={isEditable}
                />
                <ExpandableRowContent
                  title="Citizenship:"
                  value={aml?.nationalityId as string}
                  risk={
                    aml?.nationalityRisk?.toString() !== 'null' &&
                    aml?.nationalityRisk?.toString() !== 'undefined'
                      ? (aml?.nationalityRisk?.toString() as string)
                      : '-'
                  }
                  list={countries}
                  name="citizenship"
                  isEditable={isEditable}
                />
              </>
            )}
            {type === ApplicationType.CORPORATE && (
              <>
                <ExpandableRowContent
                  title="Country of incorporation:"
                  value={aml?.countryOfIncorporationId as string}
                  risk={
                    aml?.countryOfIncorporationRisk?.toString() !== 'null' &&
                    aml?.countryOfIncorporationRisk?.toString() !== 'undefined'
                      ? (aml?.countryOfIncorporationRisk?.toString() as string)
                      : '-'
                  }
                  list={countries}
                  name="countryOfIncorporation"
                  isEditable={isEditable}
                />
                <ExpandableRowContent
                  title="Citizenship of company officials"
                  value=""
                  risk={
                    aml?.officialRiskTotal?.toString() !== 'null' &&
                    aml?.officialRiskTotal?.toString() !== 'undefined'
                      ? (aml?.officialRiskTotal?.toString() as string)
                      : '-'
                  }
                  list={countries}
                  name="citizenshipOfCompanyOfficials"
                  officialRiskItems={aml?.officialRiskItems}
                  isEditable={isEditable}
                  withoutOpacity
                />
              </>
            )}
          </ExpandableRow>
          {type === ApplicationType.INDIVIDUAL && (
            <TypeOfClient
              expandedRows={expandedRows}
              setExpandedRows={setExpandedRows}
              values={aml}
              // onSend={() => {}}
              // onDelete={() => {}}
              isEditable={isEditable}
            />
          )}

          <DeliveryChannels
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            values={aml}
            // onSend={() => {}}
            // onDelete={() => {}}
            isEditable={isEditable}
            type={type}
          />
          <Products
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            values={aml}
            // onSend={() => {}}
            // onDelete={() => {}}
            isEditable={isEditable}
            type={type}
          />
          {type === ApplicationType.CORPORATE && (
            <>
              <DateOfIncorporation
                expandedRows={expandedRows}
                setExpandedRows={setExpandedRows}
                values={aml}
                // onSend={() => {}}
                // onDelete={() => {}}
                isEditable={isEditable}
              />
              <SicCodes
                expandedRows={expandedRows}
                setExpandedRows={setExpandedRows}
                values={aml}
                // onSend={() => {}}
                // onDelete={() => {}}
                isEditable={isEditable}
              />
            </>
          )}
          {type === ApplicationType.INDIVIDUAL && (
            <SourceOfFunds
              expandedRows={expandedRows}
              setExpandedRows={setExpandedRows}
              values={aml}
              // onSend={() => {}}
              // onDelete={() => {}}
              isEditable={isEditable}
            />
          )}
          <ExpandableRow
            name={RiskScoringTypes.MONTHLY_VOLUMES}
            title="Turnover"
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            totalRisk={aml?.monthlyTurnoverRangeRisk as number}
            // comments={[]}
            // onSend={() => {}}
            // onDelete={() => {}}
          >
            <ExpandableRowContent
              title="Turnover:"
              value={aml?.monthlyTurnoverRangeId as string}
              risk={
                aml?.monthlyTurnoverRangeRisk?.toString() !== 'null' &&
                aml?.monthlyTurnoverRangeRisk?.toString() !== 'undefined'
                  ? (aml?.monthlyTurnoverRangeRisk?.toString() as string)
                  : '-'
              }
              list={
                type === ApplicationType.INDIVIDUAL
                  ? monthlyTurnover
                  : monthlyTurnoverCorporate
              }
              name="monthlyTurnoverRangeId"
              isEditable={isEditable}
            />
          </ExpandableRow>
        </div>
      )}
      <Popup
        title={
          aml?.clientScoreType === ClientScoreTypeEnum.MANUAL
            ? 'Customer Application Risk Score was set manually. The update will return Customer Risk Score to the automatically calculated values'
            : 'Customer application risk score will updated'
        }
        onCancelClick={() => setPopupVisible(false)}
        onOkClick={() => onUpdate && onUpdate()}
        cancelButtonText="Cancel"
        okButtonText={requestInProgress ? 'Loading...' : 'Update'}
        isVisible={popupVisible}
      />
    </>
  );
};

export default ApplicationRiskScore;
